<template>
  <sp-modal
    width="40rem"
    persistent
    closeable
    expand-height-max
    no-mobile-content-padding
    :open="open"
    :modal-title="modalTitle"
    no-scrollable-content
    @update-open="handleModalUpdateOpen"
    @close="handleModalClose"
    @keydown.enter="submitForm"
  >
    <button
      v-if="isActivatorAsLink"
      ref="activator"
      slot="activator"
      type="button"
      class="activator-link"
      :data-testid="TestId.events.waitlistJoin"
    >
      {{ activatorText }}
    </button>
    <sp-button
      v-else
      ref="activator"
      slot="activator"
      class="activator"
      block-sm
      :color="activatorColor"
      :variant="activatorVariant"
      :data-testid="TestId.events.waitlistJoin"
    >
      {{ activatorText }}
    </sp-button>

    <sp-alert v-if="error" slot="alert" type="error" variant="elevated" closeable @close="error = null">
      {{ error }}
    </sp-alert>

    <WaitlistPanel :preamble="preamble" :info="info">
      <div ref="slotContainer" class="form-slot">
        <slot name="form"></slot>
      </div>
    </WaitlistPanel>

    <sp-container slot="actions" flex>
      <sp-spacer></sp-spacer>
      <sp-button color="primary" :loading="loading" :data-testid="TestId.events.waitlistSubmit" @click="submitForm">
        Join waitlist
      </sp-button>
    </sp-container>
  </sp-modal>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from "vue";
import { TestId } from "#/test-ids";
import { useExpose } from "#ce/composables/useExpose";
import { createCtaActivator } from "#ce/utils/cta";
import { initializeDateMasks } from "#ce/utils/date-mask";
import { initializePhoneMasks } from "#ce/utils/phone-mask";
import { toBoolean } from "#ce/utils/props";
import WaitlistPanel from "./components/WaitlistPanel.ce.vue";
import { useWaitlistStore } from "./store/waitlist";
import { props as propsDefinitions } from "./props.js";

const emit = defineEmits(["update-open"]);

const props = defineProps(propsDefinitions);

const isActivatorAsLink = computed(() => toBoolean(props.activatorAsLink));

const waitlistStore = useWaitlistStore();

const open = ref(false);
const loading = ref(false);
const error = ref(null);

const activator = useTemplateRef("activator");

const slotContainer = useTemplateRef("slotContainer");
const formSlot = ref(null);
const formElement = ref(null);

let dateMaskCleanup;
let phoneMaskCleanup;

const { exposeProperties } = useExpose();

watch(open, (value) => emit("update-open", value));

/**
 * Returns the CTA activator element
 * This is used to expose the activator button to the parent component, so it can be used to open the modal from
 * outside the component.
 */
function getCtaActivator() {
  const ctaActivator = createCtaActivator(props.mobileCtaActivatorText ?? props.activatorText);
  useEventListener(ctaActivator, "click", () => (open.value = true));
  return ctaActivator;
}

exposeProperties({
  ctaActivator: {
    get: getCtaActivator,
  },
});

watch(slotContainer, (value) => {
  formSlot.value = value?.querySelector("slot[name=form]");
});

/**
 * In order to access the form element, we need to wait for the slot to be populated.
 * We listen for the slotchange event, extract the form element, and store it in the form element ref.
 */
useEventListener(formSlot, "slotchange", () => {
  const slotContent = formSlot.value?.assignedElements?.()?.at(0);
  formElement.value = slotContent?.querySelector("form");
  removeUnwantedFormElements();
  applyInputMasks();
  waitlistStore.setFormElement(formElement.value);
});

onBeforeUnmount(removeInputMasks);

function applyInputMasks() {
  removeInputMasks();
  dateMaskCleanup = initializeDateMasks(formElement.value);
  phoneMaskCleanup = initializePhoneMasks(formElement.value);
}

function removeInputMasks() {
  dateMaskCleanup?.();
  phoneMaskCleanup?.();
}

function removeUnwantedFormElements() {
  formElement.value?.querySelector("[type=submit]")?.remove();
  formElement.value?.querySelector("[data-behaviour=preview-form]")?.remove();
}

function handleModalUpdateOpen({ detail }) {
  const [value] = detail;
  open.value = value;
}

function handleModalClose() {
  reset();
}

async function submitForm() {
  if (!waitlistStore.isValid()) {
    return;
  }

  try {
    loading.value = true;
    const confirmationUrl = await waitlistStore.submitForm();
    open.value = false;
    nextTick(() => (document.location.href = confirmationUrl));
  } catch (err) {
    console.error(err.message);
    error.value = err?.message ?? "An error occurred. Please try again later.";
    loading.value = false;
  }
}

function reset() {
  waitlistStore.reset();
  loading.value = false;
  error.value = null;
}
</script>

<style>
:host {
  /* Read on the host, so a theme's `--sp-comp-modal-header-*` still reaches the modal while the waitlist tokens are unset. */
  --waitlist-modal-header-background-color: var(
    --sp-comp-waitlist-modal-header-background-color,
    var(--sp-comp-modal-header-background-color)
  );
  --waitlist-modal-header-color: var(--sp-comp-waitlist-modal-header-color, var(--sp-comp-modal-header-color));
  --dialog-height: 94dvh;
  --dialog-min-height: 14rem;
  --dialog-max-height: 58rem;
  --sp-comp-dialog-content-height: 100%;

  display: block;
}

sp-modal {
  --sp-page-builder-form-fields-max-width: 100%;
  --sp-comp-modal-header-background-color: var(--waitlist-modal-header-background-color);
  --sp-comp-modal-header-color: var(--waitlist-modal-header-color);
  /* Fills the fixed-height dialog (--dialog-height / -max-height). Both the card
     height and max-height are 100% because max-height overrides height in CSS. */
  --sp-comp-modal-card-height: 100%;
  --sp-comp-modal-card-max-height: 100%;
  --sp-comp-modal-actions-padding-block: var(--sp-ref-scale-16) var(--sp-ref-scale-24);
}

sp-heading[level="2"] {
  margin-top: var(--sp-ref-scale-8);
  margin-bottom: calc(var(--sp-ref-scale-16) * 1.625);
}

.activator {
  --bg-color: var(--sp-comp-waitlist-activator-bg-color, var(--sp-sys-color-warning));
  --hover-bg-color: var(--sp-comp-waitlist-activator-hover-bg-color, var(--bg-color));
  --text-color: var(--sp-comp-waitlist-activator-text-color, var(--sp-sys-color-on-surface));
  --hover-text-color: var(--sp-comp-waitlist-activator-hover-text-color, var(--text-color));
  --activator-min-width: var(--sp-comp-waitlist-activator-width, 100%);
  --sp-comp-button-width: var(--activator-min-width);
  --sp-comp-button-min-width: var(--activator-min-width);

  display: flex;
  place-self: var(--sp-comp-waitlist-activator-place-self, auto);
  min-width: var(--activator-min-width);
}

.activator-link {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: center;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
}

/* Own typographic baseline so the form doesn't inherit the mounting context's font
   (e.g. an events list result card's smaller, heavier text). */
.form-slot {
  font: var(--sp-sys-typescale-body-short);
}
</style>
